import React from 'react';
// RESPONSIVE CHANGE: Import necessary hooks and components from MUI for responsiveness and the card layout
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { Col, DatePicker, Form, Input, Row, Select } from 'antd';
import { NumericFormat } from 'react-number-format';
import MainCard from 'components/MainCard';
import { withValueCap } from 'pages/globals/global';
import dayjs from 'dayjs';
import { constantFormValues } from '../../root';
import { utcConverter } from '../LeaseUtils/helper';
import { useSelector } from 'react-redux';

// RESPONSIVE CHANGE: A helper component for the mobile card view to keep the main component cleaner
const MobileRowCard = ({
						   field,
						   index,
						   screen,
						   rowIndex,
						   initialChangeLength,
						   handleInputMonthChange,
						   getMaxDateForRow,
					   }) => {
	const form = Form.useFormInstance();
	const maxDate = getMaxDateForRow(index);
	const orderedKeys = constantFormValues?.changes?.orderedRowHeader;

	// A helper to render each field in the card
	const renderField = (key) => {
		const fieldConfig = constantFormValues.changes.changeList[key];
		let inputComponent;

		// Recreate the input components from the original table
		switch (key) {
			case 'inputChangeEffectiveDate':
				inputComponent = (
					<Form.Item style={{ margin: 0 }} name={[field.name, fieldConfig.key]} rules={fieldConfig.rules}>
						<DatePicker
							disabled={
								index === 0
									? true
									: screen === 'REVISE'
										? true
										: screen === 'VIEW'
											? true
											: screen === 'EDIT'
												? index < initialChangeLength
												: false
							}
							onChange={(date, dateString) => {
								if (date === null || date === undefined || dateString === '') {
									handleInputMonthChange(null, index);
								} else {
									handleInputMonthChange(date, index);
								}
							}}
							format="YYYY-MM-DD"
							style={{ width: '100%', height: '32px' }}
							className="antd-datePicker"
							disabledDate={(current) => {
								if (!current) return false;
								const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));
								const isBefore = current.isBefore(leaseStartDate, 'day');
								let isAfter = false;
								if (maxDate) {
									isAfter = current.isAfter(maxDate, 'day');
								}
								return isBefore || isAfter;
							}}
						/>
					</Form.Item>
				);
				break;
			case 'inputChangeDescription':
				inputComponent = (
					<Form.Item style={{ margin: 0 }} name={[field.name, fieldConfig.key]}>
						<Select disabled showSearch style={{ width: '100%' }} filterOption={true}
								options={fieldConfig.options} />
					</Form.Item>
				);
				break;
			case 'inputChangeLeaseTerm':
				inputComponent = (
					<Form.Item style={{ margin: 0 }} name={[field.name, fieldConfig.key]}>
						<Input
							disabled
							type="number"
							style={{
								marginBottom: 0,
								textAlign: 'right',
								color:
									index !== 0 &&
									form.getFieldValue(['changes', index, 'inputChangeLeaseTerm']) !==
									form.getFieldValue(['changes', index - 1, 'inputChangeLeaseTerm'])
										? 'blue'
										: 'black',
							}}
						/>
					</Form.Item>
				);
				break;
			case 'inputChangeDiscountRate':
				inputComponent = (
					<Form.Item style={{ margin: 0 }} name={[field.name, fieldConfig.key]}>
						<NumericFormat
							size="large"
							decimalScale={2}
							placeholder="0.00"
							fixedDecimalScale
							thousandSeparator=","
							isAllowed={withValueCap}
							style={{
								textAlign: 'right',
								height: '32px',
								borderRadius: '4px',
								fontSize: '13px',
								color:
									index !== 0 &&
									form.getFieldValue(['changes', index, 'inputChangeDiscountRate']) !==
									form.getFieldValue(['changes', index - 1, 'inputChangeDiscountRate'])
										? 'blue'
										: 'black',
							}}
							customInput={Input}
							disabled={screen === 'CREATE' || screen === 'VIEW' ? true : screen !== 'CREATE' && index < rowIndex ? true : false}
						/>
					</Form.Item>
				);
				break;
			case 'paymentFrequency':
			case 'paymentTime':
				inputComponent = (
					<Form.Item style={{ margin: 0 }} name={[field.name, fieldConfig.key]}>
						<Select disabled showSearch style={{ width: '100%' }} filterOption={true}
								options={fieldConfig.options} />
					</Form.Item>
				);
				break;
			default:
				inputComponent = null;
		}

		return (
			<Box key={key} sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				py: 1,
				borderBottom: '1px solid #f0f0f0',
			}}>
				<Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
					{fieldConfig.label}
				</Typography>
				<Box sx={{ width: '55%', flexShrink: 0 }}>{inputComponent}</Box>
			</Box>
		);
	};

	return (
		<MainCard sx={{ mb: 2, borderColor: '#d9d9d9' }} contentSX={{ p: '16px !important' }}>
			{orderedKeys.map((key) => renderField(key))}
		</MainCard>
	);
};

const IFRS16FieldsModifications = ({ screen, rowIndex, initialChangeLength = 0 }) => {
	const form = Form.useFormInstance();
	// RESPONSIVE CHANGE: Detect if the screen is mobile-sized
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Change breakpoint as needed ('sm', 'md', etc.)
	const { maxRecordedDate } = useSelector((state) => state.lease);
	// console.log(maxRecordedDate?.minimumDateLimit, 'maxRecordedDate223344');
	// --- No changes to any of the logic ---
	const handleInputMonthChange = (e, index) => {
		if (!e) {
			const currentChanges = form.getFieldValue('changes') || [];
			if (currentChanges[index]) {
				const updatedChanges = [...currentChanges];
				updatedChanges[index] = {
					...updatedChanges[index],
					inputChangeMonth: null,
					inputChangeEffectiveDate: null,
				};
				form.setFieldsValue({ changes: updatedChanges });
			}
			return;
		}
		const effectiveDate = dayjs(e);
		const leaseStart = dayjs(form.getFieldValue('leaseStartDate'));
		let monthDifference = (effectiveDate.year() - leaseStart.year()) * 12 + (effectiveDate.month() - leaseStart.month());
		monthDifference += 1;
		if (monthDifference < 0) {
			monthDifference = 0;
		}
		const currentChanges = form.getFieldValue('changes') || [];
		if (currentChanges[index]) {
			const updatedChanges = [...currentChanges];
			updatedChanges[index] = {
				...updatedChanges[index],
				inputChangeMonth: monthDifference,
				inputChangeEffectiveDate: utcConverter(effectiveDate),
			};
			form.setFieldsValue({ changes: updatedChanges });
		}
	};
	const getMaxDateForRow = (index) => {
		if (index === 0) {
			return null;
		}
		const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));
		if (!leaseStartDate) return null;
		const changes = form.getFieldValue('changes');
		if (!changes || !changes[index - 1]) return null;
		const prevRowLeaseTerm = changes[index - 1].inputChangeLeaseTerm;
		if (!prevRowLeaseTerm) return null;
		return leaseStartDate.add(parseInt(prevRowLeaseTerm), 'month').subtract(1, 'day');
	};
	// --- End of logic section ---

	return (
		<Row gutter={[16, 16]}>
			<Col xs={24}>
				<Form.List name="changes">
					{(fields) => {
						// RESPONSIVE CHANGE: Conditionally render based on screen size
						return isMobile ? (
							// --- MOBILE VIEW: Renders a list of cards ---
							<>
								{fields?.map((field, index) => (
									<MobileRowCard
										key={field.key}
										field={field}
										index={index}
										screen={screen}
										rowIndex={rowIndex}
										initialChangeLength={initialChangeLength}
										handleInputMonthChange={handleInputMonthChange}
										getMaxDateForRow={getMaxDateForRow}
									/>
								))}
							</>
						) : (
							// --- DESKTOP VIEW: Renders the original table ---
							<MainCard content={false} sx={{ mb: 2, mt: '12px', borderColor: '#d9d9d9' }}>
								<TableContainer
									sx={{
										width: '100%',
										overflowX: 'auto',
										position: 'relative',
										display: 'block',
										'& td, & th': { whiteSpace: 'nowrap' },
										'& td': { borderTop: '0px', borderLeft: '0px', borderBottom: '0px' },
										'& td:not(:last-child)': { borderRight: '1px solid #e0e0e0' },
										'& tr:not(:last-child)': { borderBottom: '1px solid #e0e0e0' },
										'& th': { borderBottom: '1px solid #e0e0e0' },
									}}
								>
									<Table sx={{
										'& .MuiTableBody-root .MuiTableCell-root': { px: 0.5, py: 0.5 },
										tableLayout: 'fixed',
									}}>
										<TableHead>
											<TableRow>
												{constantFormValues?.changes?.orderedRowHeader.map((key) => (
													<TableCell
														key={constantFormValues?.changes?.changeList[key].key}
														sx={{ fontSize: '14px', backgroundColor: '#e1f0ff !important' }}
														align="center"
													>
														{constantFormValues?.changes?.changeList[key].label}
													</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{fields?.map((field, index) => {
												const maxDate = getMaxDateForRow(index);
												return (
													// The entire table structure and its logic remain untouched for the desktop view
													<TableRow key={index} style={{ height: '60px' }}>
														{/* All the original <TableCell> and <Form.Item> components go here, exactly as they were */}
														{/* Effective Date */}
														<TableCell style={{ position: 'relative' }}>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].key,
																]}
																rules={constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].rules}
															>
																<DatePicker
																	disabled={
																		index === 0
																			? true
																			: screen === 'REVISE'
																				? true
																				: screen === 'VIEW'
																					? true
																					: screen === 'EDIT'
																						? index < initialChangeLength
																						: false
																	}
																	onChange={(date, dateString) => {
																		if (date === null || date === undefined || dateString === '') {
																			handleInputMonthChange(null, index);
																		} else {
																			handleInputMonthChange(date, index);
																		}
																	}}
																	format="YYYY-MM-DD"
																	style={{ width: '100%', height: '32px' }}
																	className="antd-datePicker"
																	disabledDate={(current) => {
																		// Rule 0: Always handle the case where the current date is not provided.
																		if (!current) {
																			return false;
																		}

																		// --- SETUP ---
																		// Get values from the form.
																		const leaseStartDateValue = form.getFieldValue('leaseStartDate');

																		// Robustness: If there's no start date yet, don't disable anything.
																		if (!leaseStartDateValue) {
																			return false;
																		}

																		const leaseStartDate = dayjs(leaseStartDateValue);

																		// Robustness: If the date is invalid, don't crash.
																		if (!leaseStartDate.isValid()) {
																			return false;
																		}

																		// --- RULE 1 & 2: DETERMINE THE MINIMUM ALLOWED DATE ("FLOOR") ---
																		let minimumAllowedDate = leaseStartDate; // Default to the lease start date.

																		// This logic assumes `maxRecordedDate` is passed as a prop,
																		// likely when your component's index >= 2.
																		if (maxRecordedDate?.minimumDateLimit) {
																			// Override the minimum date based on the last recorded action.
																			minimumAllowedDate = dayjs(maxRecordedDate.minimumDateLimit)
																				.add(1, 'month')
																				.startOf('month');
																		}
																		const isBeforeMinimum = current.isBefore(minimumAllowedDate, 'day');


																		// --- RULE 3: DETERMINE THE MAXIMUM ALLOWED DATE ("CEILING") ---
																		let isAfterMaximum = false;
																		if (maxDate) {
																			isAfterMaximum = current.isAfter(dayjs(maxDate), 'day');
																		}


																		// --- RULE 4: CHECK FOR THE CORRECT DAY OF THE MONTH ---
																		const requiredDayOfMonth = leaseStartDate.date();
																		const isWrongDayOfMonth = current.date() !== requiredDayOfMonth;


																		// --- FINAL DECISION ---
																		// Disable the date if it violates ANY of the rules.
																		return isBeforeMinimum || isAfterMaximum || isWrongDayOfMonth;
																	}}
																/>
															</Form.Item>
														</TableCell>
														{/* Description */}
														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeDescription'].key,
																]}
															>
																<Select
																	disabled
																	showSearch
																	style={{ textAlign: 'center' }}
																	filterOption={true}
																	options={
																		constantFormValues?.changes?.changeList['inputChangeDescription'].options
																	}
																/>
															</Form.Item>
														</TableCell>
														{/* Lease Term */}
														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeLeaseTerm'].key,
																]}
															>
																<Input
																	disabled
																	type="number"
																	style={{
																		marginBottom: 0,
																		textAlign: 'center',
																		color:
																			index !== 0
																				? form.getFieldValue(['changes', index, 'inputChangeLeaseTerm']) ===
																				form.getFieldValue(['changes', index - 1, 'inputChangeLeaseTerm'])
																					? 'black'
																					: 'blue'
																				: 'black',
																	}}
																/>
															</Form.Item>
														</TableCell>
														{/* Discount Rate */}
														<TableCell align="center">
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeDiscountRate'].key,
																]}
															>
																<NumericFormat
																	size="large"
																	decimalScale={2}
																	placeholder="0.00"
																	fixedDecimalScale
																	thousandSeparator=","
																	isAllowed={withValueCap}
																	style={{
																		textAlign: 'center',
																		height: '32px',
																		borderRadius: '4px',
																		fontSize: '13px',
																		color:
																			index !== 0
																				? form.getFieldValue([
																					'changes',
																					index,
																					'inputChangeDiscountRate',
																				]) ===
																				form.getFieldValue([
																					'changes',
																					index - 1,
																					'inputChangeDiscountRate',
																				])
																					? 'black'
																					: 'blue'
																				: 'black',
																	}}
																	customInput={Input}
																	disabled={
																		screen === 'CREATE' || screen === 'VIEW'
																			? true
																			: screen !== 'CREATE'
																				? index < rowIndex
																					? true
																					: false
																				: false
																	}
																/>
															</Form.Item>
														</TableCell>
														{/* Payment Frequency */}
														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[field.name, constantFormValues?.changes?.changeList['paymentFrequency'].key]}
															>
																<Select
																	disabled
																	showSearch
																	style={{ textAlign: 'center' }}
																	filterOption={true}
																	options={constantFormValues?.changes?.changeList['paymentFrequency'].options}
																/>
															</Form.Item>
														</TableCell>
														{/* Payment Time */}
														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[field.name, constantFormValues?.changes?.changeList['paymentTime'].key]}
															>
																<Select
																	disabled
																	showSearch
																	style={{ textAlign: 'center' }}
																	filterOption={true}
																	options={constantFormValues?.changes?.changeList['paymentTime'].options}
																/>
															</Form.Item>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</MainCard>
						);
					}}
				</Form.List>
			</Col>
		</Row>
	);
};

export default IFRS16FieldsModifications;